import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Row, Col, Card, Badge, Button, Spinner, Alert,
  Form, InputGroup, Dropdown
} from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import {
  listTools,
  retireTool
} from '../../services/toolAssignmentService';
import { getUsersByRole } from '../../services/userService';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import ToolRegisterModal from './components/ToolRegisterModal';
import ToolEditModal from './components/ToolEditModal';
import ToolAssignModal from './components/ToolAssignModal';
import ToolReturnModal from './components/ToolReturnModal';
import ToolEmptyState from './components/ToolEmptyState';
import DestructiveConfirmModal from '../../components/common/DestructiveConfirmModal';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './ToolList.css';

const STATUS_LABELS = {
  available: 'Disponible',
  assigned: 'Asignada',
  maintenance: 'Mantenimiento',
  retired: 'Retirada'
};
const STATUS_COLORS = {
  available: 'success',
  assigned: 'primary',
  maintenance: 'warning',
  retired: 'secondary'
};
const CONDITION_LABELS = {
  good: 'Buena',
  damaged: 'Dañada',
  lost: 'Perdida',
  maintenance: 'Mantenimiento'
};
const CONDITION_COLORS = {
  good: 'success',
  damaged: 'warning',
  lost: 'danger',
  maintenance: 'info'
};

const ToolList = () => {
  const navigate = useNavigate();
  const { isAdmin, isSupervisor } = useAuth();
  // #798 — el SUPERVISOR registra herramientas nuevas (decision de Luis; la
  // base: policy `supervisor_insert_tools`). Ve el catalogo (`tools_select` ya
  // se lo concedia y el sidebar ya lo traia aca, a un "Acceso restringido") y
  // el boton de alta. NADA MAS: editar, asignar, forzar devolucion, marcar
  // perdida y retirar son UPDATE sobre `tools`, que sigue en `is_admin()`, y la
  // ficha (`ToolDetail`) sigue admin-only. Por eso no hay menu ni click en fila.
  const canRegister = isAdmin || isSupervisor;
  const queryClient = useQueryClient();
  const { viewMode, setViewMode, isCardView } = useViewToggle();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [holderFilter, setHolderFilter] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editTool, setEditTool] = useState(null);
  const [assignTool, setAssignToolState] = useState(null);
  const [returnState, setReturnState] = useState(null);
  const [retireTarget, setRetireTarget] = useState(null);

  // Opens the register modal on load when arriving from the "Registrar
  // herramienta" command in the Global Search / Command Palette (#419),
  // e.g. navigate('/herramientas?action=register'). Strips the param right
  // after so a manual page refresh doesn't re-trigger the modal.
  useEffect(() => {
    if (searchParams.get('action') === 'register') {
      setRegisterOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['tools', search, statusFilter],
    queryFn: () => listTools({
      search: search || undefined,
      status: statusFilter || undefined,
      limit: 200
    }),
    enabled: canRegister,
    staleTime: 30 * 1000
  });

  const { data: techniciansData } = useQuery({
    queryKey: ['technicians-list'],
    queryFn: () => getUsersByRole('technician'),
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000
  });

  const retireMutation = useMutation({
    mutationFn: ({ tool, reason }) => retireTool(tool.id, { reason }),
    onSuccess: ({ error: err }) => {
      if (err) { toast.error(err.message); return; }
      toast.success('Herramienta retirada del inventario.');
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      queryClient.invalidateQueries({ queryKey: ['all-tools'] });
      setRetireTarget(null);
    },
    onError: (e) => toast.error(e.message)
  });

  const tools = useMemo(() => data?.data || [], [data]);
  const technicians = techniciansData?.data || techniciansData || [];

  const categories = useMemo(
    () => [...new Set(tools.map(t => t._categoryLabel).filter(Boolean))].sort(),
    [tools]
  );

  const filteredTools = useMemo(() => tools.filter(t => {
    if (categoryFilter && t._categoryLabel !== categoryFilter) return false;
    if (holderFilter) {
      const holderId = t._active_assignment?.holder?.id;
      if (holderId !== holderFilter) return false;
    }
    return true;
  }), [tools, categoryFilter, holderFilter]);

  const stats = useMemo(() => tools.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {}), [tools]);

  const clearFilters = () => {
    setSearch(''); setStatusFilter(''); setCategoryFilter(''); setHolderFilter('');
  };

  const handleRetire = (tool) => setRetireTarget(tool);

  if (!canRegister) {
    return <Alert variant="danger" className="m-3">Acceso restringido a administradores y supervisores.</Alert>;
  }

  // Solo el admin abre la ficha: `ToolDetail` corta al resto.
  const abrirDetalle = (tool) => { if (isAdmin) navigate(`/herramientas/${tool.id}`); };
  const estiloFila = isAdmin ? { cursor: 'pointer' } : undefined;

  const renderKebab = (tool) => !isAdmin ? null : (
    <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
      <Dropdown.Toggle
        variant="link"
        className="p-0 text-muted"
        style={{ boxShadow: 'none' }}
        aria-label={`Opciones para ${tool.name}`}
      >
        <i className="bi bi-three-dots-vertical" />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => navigate(`/herramientas/${tool.id}`)}>
          <i className="bi bi-eye me-2" />Ver detalle
        </Dropdown.Item>
        <Dropdown.Divider />
        {tool.status !== 'retired' && (
          <Dropdown.Item
            onClick={() => setAssignToolState(tool)}
            disabled={tool.status === 'assigned'}
          >
            <i className="bi bi-person-check me-2 text-primary" />Asignar
          </Dropdown.Item>
        )}
        {tool._active_assignment && (
          <Dropdown.Item
            onClick={() => setReturnState({
              assignment: { ...tool._active_assignment, tool_id: tool.id, _toolName: tool.name, _toolSerial: tool.serial_number },
              mode: 'return'
            })}
          >
            <i className="bi bi-box-arrow-in-left me-2 text-success" />Forzar devolución
          </Dropdown.Item>
        )}
        <Dropdown.Item onClick={() => setEditTool(tool)}>
          <i className="bi bi-pencil me-2" />Editar
        </Dropdown.Item>
        {tool._active_assignment && (
          <Dropdown.Item
            className="text-danger"
            onClick={() => setReturnState({
              assignment: { ...tool._active_assignment, tool_id: tool.id, _toolName: tool.name, _toolSerial: tool.serial_number },
              mode: 'lost'
            })}
          >
            <i className="bi bi-exclamation-triangle me-2" />Marcar perdida
          </Dropdown.Item>
        )}
        {tool.status !== 'retired' && (
          <>
            <Dropdown.Divider />
            <Dropdown.Item className="text-danger" onClick={() => handleRetire(tool)}>
              <i className="bi bi-archive me-2" />Retirar
            </Dropdown.Item>
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );

  const renderCard = (tool) => (
    <Col key={tool.id} xs={12} sm={6} lg={4} xl={3} className="mb-3">
      <Card
        className="h-100 modern-card tool-card"
        onClick={() => abrirDetalle(tool)}
        style={estiloFila}
      >
        <div className="tool-card-photo-wrap">
          {tool.image_url ? (
            <img src={tool.image_url} alt={tool.name} className="tool-card-photo" />
          ) : (
            <div className="tool-card-photo-placeholder">
              <i className="bi bi-tools" />
            </div>
          )}
          <div className="position-absolute top-0 end-0 p-2 d-flex flex-column align-items-end gap-1">
            <Badge bg={STATUS_COLORS[tool.status]}>{STATUS_LABELS[tool.status]}</Badge>
          </div>
        </div>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-1">
            <Card.Title className="mb-0 fs-6 fw-bold text-truncate" title={tool.name}>
              {tool.name}
            </Card.Title>
            {renderKebab(tool)}
          </div>
          <small className="text-muted font-monospace d-block">{tool.serial_number}</small>
          {(tool.brand || tool.model) && (
            <small className="text-muted d-block text-truncate">
              {tool.brand}{tool.brand && tool.model ? ' — ' : ''}{tool.model}
            </small>
          )}
          <div className="d-flex flex-wrap gap-1 mt-2">
            {tool._categoryLabel && <Badge bg="light" text="dark">{tool._categoryLabel}</Badge>}
            {tool._locationLabel && (
              <Badge bg="light" text="dark" title="Ubicación">
                <i className="bi bi-geo-alt me-1" />{tool._locationLabel}
              </Badge>
            )}
            {tool.condition && (
              <Badge bg={CONDITION_COLORS[tool.condition]}>{CONDITION_LABELS[tool.condition]}</Badge>
            )}
          </div>
          {tool._active_assignment?.holder && (
            <div className="mt-2 small">
              <i className="bi bi-person-fill me-1 text-muted" />
              <span className="text-muted">Asignada a:</span>{' '}
              <strong>{tool._active_assignment.holder.full_name}</strong>
            </div>
          )}
        </Card.Body>
      </Card>
    </Col>
  );

  const renderRow = (tool) => (
    <tr
      key={tool.id}
      onClick={() => abrirDetalle(tool)}
      style={estiloFila}
    >
      <td>
        {tool.image_url ? (
          <img src={tool.image_url} alt="" className="tool-row-thumb" />
        ) : (
          <div className="tool-row-thumb-placeholder">
            <i className="bi bi-tools" />
          </div>
        )}
      </td>
      <td>
        <strong>{tool.name}</strong><br />
        <small className="text-muted font-monospace">{tool.serial_number}</small>
      </td>
      <td>{tool._categoryLabel || '—'}</td>
      <td>
        {tool._locationLabel ? (
          <span><i className="bi bi-geo-alt me-1 text-muted" />{tool._locationLabel}</span>
        ) : <span className="text-muted">—</span>}
      </td>
      <td>{tool.brand || '—'}{tool.model ? ` / ${tool.model}` : ''}</td>
      <td><Badge bg={STATUS_COLORS[tool.status]}>{STATUS_LABELS[tool.status]}</Badge></td>
      <td><Badge bg={CONDITION_COLORS[tool.condition]}>{CONDITION_LABELS[tool.condition]}</Badge></td>
      <td>{tool._active_assignment?.holder?.full_name || <span className="text-muted">—</span>}</td>
      <td>{renderKebab(tool)}</td>
    </tr>
  );

  return (
    <DashboardLayout>
    <Container fluid className="py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h2 className="modern-title mb-0">
          <i className="bi bi-tools me-2" />Herramientas
        </h2>
        <div className="d-flex gap-2 align-items-center">
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          <Button variant="primary" onClick={() => setRegisterOpen(true)}>
            <i className="bi bi-plus-lg me-1" />Registrar herramienta
          </Button>
        </div>
      </div>

      <Row className="g-2 mb-3">
        {Object.entries(STATUS_LABELS).map(([status, label]) => (
          <Col key={status} xs={6} sm={3}>
            <Card
              className={`text-center py-2 stat-card ${statusFilter === status ? 'stat-card-active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
              style={{ cursor: 'pointer' }}
            >
              <Card.Body className="p-2">
                <div className="fw-bold fs-5">{stats[status] || 0}</div>
                <Badge bg={STATUS_COLORS[status]} className="w-100">{label}</Badge>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="g-2 mb-3">
        <Col xs={12} md={4}>
          <InputGroup>
            <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
            <Form.Control
              placeholder="Buscar por nombre..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <Button variant="outline-secondary" onClick={() => setSearch('')}>
                <i className="bi bi-x" />
              </Button>
            )}
          </InputGroup>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
            <option value="">Todas las categorías</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </Form.Select>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Form.Select value={holderFilter} onChange={e => setHolderFilter(e.target.value)}>
            <option value="">Todos los técnicos</option>
            {technicians.map(t => (
              <option key={t.id} value={t.id}>{t.full_name}</option>
            ))}
          </Form.Select>
        </Col>
        {(search || statusFilter || categoryFilter || holderFilter) && (
          <Col xs={12} md={2}>
            <Button variant="link" size="sm" onClick={clearFilters} className="text-muted">
              <i className="bi bi-x-circle me-1" />Limpiar
            </Button>
          </Col>
        )}
      </Row>

      {isLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      )}
      {error && <Alert variant="danger">Error al cargar: {error.message}</Alert>}

      {!isLoading && filteredTools.length === 0 && (
        tools.length === 0 ? (
          <ToolEmptyState
            variant="admin_no_tools"
            onPrimary={() => setRegisterOpen(true)}
          />
        ) : (
          <ToolEmptyState
            variant="filter_no_results"
            searchTerm={search}
            onPrimary={clearFilters}
          />
        )
      )}

      {!isLoading && filteredTools.length > 0 && (
        isCardView ? (
          <Row>{filteredTools.map(renderCard)}</Row>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Foto</th>
                  <th>Herramienta</th>
                  <th>Categoría</th>
                  <th>Ubicación</th>
                  <th>Marca/Modelo</th>
                  <th>Estado</th>
                  <th>Condición</th>
                  <th>Holder</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>{filteredTools.map(renderRow)}</tbody>
            </table>
          </div>
        )
      )}

      <ToolRegisterModal show={registerOpen} onHide={() => setRegisterOpen(false)} soloAlta={!isAdmin} />
      <ToolEditModal show={!!editTool} onHide={() => setEditTool(null)} tool={editTool} />
      <ToolAssignModal show={!!assignTool} onHide={() => setAssignToolState(null)} tool={assignTool} />
      <ToolReturnModal
        show={!!returnState}
        onHide={() => setReturnState(null)}
        assignment={returnState?.assignment}
        mode={returnState?.mode}
      />

      <DestructiveConfirmModal
        show={!!retireTarget}
        onHide={() => setRetireTarget(null)}
        title="Retirar herramienta del inventario"
        icon="bi-archive"
        variant="danger"
        description={
          <>
            Vas a retirar <strong>{retireTarget?.name}</strong>
            {retireTarget?.serial_number && (
              <> <code className="ms-1">({retireTarget.serial_number})</code></>
            )} del catálogo activo.
          </>
        }
        consequences={[
          'La herramienta pasa a estado "retirada" y no aparece más en filtros de bodega.',
          'Las asignaciones históricas y el audit log se conservan.',
          'Para volver a usarla, un admin debe reactivarla manualmente o registrarla como nueva.'
        ]}
        confirmText={retireTarget?.serial_number}
        confirmLabel="serial de la herramienta"
        reasonRequired
        reasonLabel="Motivo del retiro"
        reasonPlaceholder="Fin de vida útil, robo, daño irreparable, reemplazo..."
        submitLabel="Confirmar retiro"
        onConfirm={({ reason }) => retireMutation.mutate({ tool: retireTarget, reason })}
        pending={retireMutation.isPending}
      />
    </Container>
    </DashboardLayout>
  );
};

export default ToolList;
